import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const login = (data) => api.post('/auth/login', data);
export const signup = (data) => api.post('/auth/signup', data);
export const getMe = () => api.get('/auth/me');
export const updateProfile = (data) => api.put('/auth/profile', data);
export const uploadProfilePicture = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/auth/profile-picture', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteProfilePicture = () => api.delete('/auth/profile-picture');

// Google OAuth
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const processGoogleSession = (sessionId) => api.post('/auth/google/session', { session_id: sessionId });
export const logout = () => api.post('/auth/logout');

// Subscriptions
export const getSubscriptionTiers = () => api.get('/subscriptions/tiers');
export const createSubscriptionCheckout = (tier) => {
  const origin_url = window.location.origin;
  return api.post('/subscriptions/checkout', { tier, origin_url });
};
export const getMySubscription = () => api.get('/subscriptions/my');

// Admin Subscription Plans
export const getAllSubscriptionPlans = () => api.get('/subscriptions/plans');
export const createSubscriptionPlan = (data) => api.post('/subscriptions/plans', data);
export const updateSubscriptionPlan = (planId, data) => api.put(`/subscriptions/plans/${planId}`, data);
export const deleteSubscriptionPlan = (planId) => api.delete(`/subscriptions/plans/${planId}`);
export const seedDefaultPlans = () => api.post('/subscriptions/plans/seed-defaults');

// Donations
export const getSuggestedCreators = (limit = 10) => api.get('/donations/suggested-creators', { params: { limit } });
export const setupDonations = (data) => api.post('/donations/setup', data);
export const getMyDonationAllocations = () => api.get('/donations/my-allocations');
export const updateDonations = (data) => api.put('/donations/update', data);

// Comics
export const getComics = (params) => api.get('/comics', { params });
export const getTrendingComics = (limit = 10) => api.get('/comics/trending', { params: { limit } });
export const getNewComics = (limit = 10) => api.get('/comics/new', { params: { limit } });
export const getComicSpotlight = () => api.get('/comics/spotlight');
export const getComic = (id) => api.get(`/comics/${id}`);
export const createComic = (data) => api.post('/comics', data);
export const updateComic = (id, data) => api.put(`/comics/${id}`, data);
export const likeComic = (id) => api.post(`/comics/${id}/like`);
export const favoriteComic = (id) => api.post(`/comics/${id}/favorite`);

// Episodes
export const getEpisodes = (comicId) => api.get(`/comics/${comicId}/episodes`);
export const getEpisode = (id) => api.get(`/episodes/${id}`);
export const createEpisode = (comicId, data) => api.post(`/comics/${comicId}/episodes`, data);
export const likeEpisode = (id) => api.post(`/episodes/${id}/like`);

// Creators
export const getCreators = (limit = 20) => api.get('/creators', { params: { limit } });
export const getCreator = (id) => api.get(`/creators/${id}`);
export const getCreatorDashboard = () => api.get('/creator/dashboard');
export const getCreatorEarnings = () => api.get('/creator/earnings');

// File Uploads
export const uploadPanel = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload/panel', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const uploadMultiplePanels = (files) => {
  const formData = new FormData();
  files.forEach(file => formData.append('files', file));
  return api.post('/upload/panels', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteUpload = (fileId) => api.delete(`/upload/${fileId}`);
export const getMyUploads = (limit = 50) => api.get('/upload/my-uploads', { params: { limit } });

// Feedback (Contributor feature)
export const submitFeedback = (data) => api.post('/feedback', data);
export const getMyFeedback = () => api.get('/feedback/my');

// Leaderboards & Prizes
export const getCreatorLeaderboard = (limit = 10) => api.get('/leaderboard/creators', { params: { limit } });
export const getReaderLeaderboard = (limit = 10) => api.get('/leaderboard/readers', { params: { limit } });
export const getCurrentWeekPrizes = () => api.get('/prizes/current-week');
export const getPrizeHistory = (limit = 20) => api.get('/prizes/history', { params: { limit } });

// Comments
export const getComments = (params) => api.get('/comments', { params });
export const createComment = (data) => api.post('/comments', data);
export const deleteComment = (id) => api.delete(`/comments/${id}`);

// Contact
export const createContactMessage = (data) => api.post('/contact', data);

// User Dashboard
export const getUserFavorites = () => api.get('/user/favorites');
export const getReadingHistory = () => api.get('/user/reading-history');

// Payments
export const getPaymentStatus = (sessionId) => api.get(`/payments/status/${sessionId}`);
export const getPaymentHistory = () => api.get('/payments/history');

// Admin
export const getAdminUsers = () => api.get('/admin/users');
export const getAdminStats = () => api.get('/admin/stats');
export const freezeUser = (userId) => api.post(`/admin/users/${userId}/freeze`);
export const deleteUser = (userId) => api.delete(`/admin/users/${userId}`);
export const updateUserTier = (userId, tier) => api.put(`/admin/users/${userId}/tier?tier=${tier}`);
export const getContactMessages = () => api.get('/admin/messages');
export const markMessageRead = (messageId) => api.put(`/admin/messages/${messageId}/read`);
export const adminGetFeedback = (status = null) => api.get('/admin/feedback', { params: { status } });
export const adminReviewFeedback = (feedbackId, status, paymentAmount = null, adminNotes = null) => 
  api.put(`/admin/feedback/${feedbackId}/review?status=${status}${paymentAmount ? `&payment_amount=${paymentAmount}` : ''}${adminNotes ? `&admin_notes=${adminNotes}` : ''}`);
export const adminAwardPrize = (prizeType, userId, amount) => 
  api.post(`/admin/prizes/award?prize_type=${prizeType}&user_id=${userId}&amount=${amount}`);

// Enhanced Admin Panel
export const getAdminDashboard = () => api.get('/admin/dashboard');
export const getAdminAnalytics = (period = '7d') => api.get('/admin/analytics', { params: { period } });

// Admin Site Settings
export const getSiteSettings = () => api.get('/admin/settings');
export const updateSiteSettings = (settings) => api.put('/admin/settings', settings);

// Admin Comics Management
export const getAdminComics = (params) => api.get('/admin/comics', { params });
export const updateAdminComic = (comicId, data) => api.put(`/admin/comics/${comicId}`, data);
export const deleteAdminComic = (comicId) => api.delete(`/admin/comics/${comicId}`);
export const featureComic = (comicId, featured = true) => api.post(`/admin/comics/${comicId}/feature?featured=${featured}`);

// Admin Bulk Actions
export const bulkComicsAction = (action, comicIds) => api.post('/admin/bulk/comics', { action, comic_ids: comicIds });
export const bulkUsersAction = (action, userIds, tier = null) => api.post('/admin/bulk/users', { action, user_ids: userIds, tier });

// Admin Activity Logs
export const getActivityLogs = (limit = 50) => api.get('/admin/activity-logs', { params: { limit } });

// Admin Announcements
export const getAnnouncements = (activeOnly = false) => api.get('/admin/announcements', { params: { active_only: activeOnly } });
export const createAnnouncement = (title, content, type = 'info', expiresAt = null) => 
  api.post('/admin/announcements', { title, content, type, is_active: true, expires_at: expiresAt });
export const deleteAnnouncement = (id) => api.delete(`/admin/announcements/${id}`);

// Admin Reports
export const getReports = (status = 'pending') => api.get('/admin/reports', { params: { status } });
export const createReport = (contentType, contentId, reason) => api.post('/admin/reports', { content_type: contentType, content_id: contentId, reason });
export const reviewReport = (reportId, action, adminNotes = null) => 
  api.put(`/admin/reports/${reportId}?action=${action}${adminNotes ? `&admin_notes=${adminNotes}` : ''}`);

// Admin Data Export
export const exportData = (dataType) => api.get(`/admin/export/${dataType}`);

// Admin Email Templates
export const getEmailTemplates = () => api.get('/admin/email-templates');
export const updateEmailTemplate = (templateId, name, subject, body) => 
  api.put(`/admin/email-templates/${templateId}?name=${encodeURIComponent(name)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);

// Visitor Analytics
export const trackPageView = (page, referrer = null) => {
  const userAgent = navigator.userAgent;
  let url = `/analytics/track?page=${encodeURIComponent(page)}`;
  if (referrer) url += `&referrer=${encodeURIComponent(referrer)}`;
  if (userAgent) url += `&user_agent=${encodeURIComponent(userAgent)}`;
  return api.post(url);
};
export const getVisitorAnalytics = (period = '7d') => api.get('/admin/visitor-analytics', { params: { period } });

// Admin Payments & Refunds
export const getAllPayments = (params) => api.get('/admin/payments', { params });
export const processRefund = (paymentId, reason = 'Customer request') => 
  api.post(`/admin/refund/${paymentId}?reason=${encodeURIComponent(reason)}`);

// Admin Free Premium Override
export const grantFreePremium = (userId, tier = 'contributor', reason = 'Admin override', expiresAt = null) => 
  api.post(`/admin/users/${userId}/free-premium?tier=${tier}&reason=${encodeURIComponent(reason)}${expiresAt ? `&expires_at=${expiresAt}` : ''}`);
export const revokeFreePremium = (userId) => api.delete(`/admin/users/${userId}/free-premium`);
export const getFreePremiumUsers = () => api.get('/admin/free-premium-users');

// Admin Management (Super Admin)
export const getAllAdmins = () => api.get('/admin/admins');
export const promoteToAdmin = (userId, role = 'admin') => api.post(`/admin/promote/${userId}?role=${role}`);
export const demoteAdmin = (userId) => api.post(`/admin/demote/${userId}`);

// Email Notifications
export const getEmailNotifications = (status = null, limit = 50) => 
  api.get('/admin/notifications', { params: { status, limit } });
export const sendBulkEmail = (subject, body, target = 'all') => 
  api.post(`/admin/send-bulk-email?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}&target=${target}`);

// System Health & Maintenance
export const getSystemHealth = () => api.get('/admin/system-health');
export const toggleMaintenanceMode = (enabled, message = 'Site under maintenance') => 
  api.post(`/admin/maintenance-mode?enabled=${enabled}&message=${encodeURIComponent(message)}`);
export const createBackup = () => api.post('/admin/backup-database');
export const getBackups = () => api.get('/admin/backups');

// Upload
export const uploadImage = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload/image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Genres
export const getGenres = () => api.get('/genres');

// AI Creator Tools
export const getSupportedLanguages = () => api.get('/ai/languages');
export const colorizeImage = (imageBase64, colorDescription) => 
  api.post('/ai/colorize', { image_base64: imageBase64, color_description: colorDescription }, { timeout: 120000 });
export const generateCover = (title, description, genre, styleNotes = null) => 
  api.post('/ai/generate-cover', { title, description, genre, style_notes: styleNotes }, { timeout: 120000 });
export const translateText = (text, sourceLanguage, targetLanguage) => 
  api.post('/ai/translate', { text, source_language: sourceLanguage, target_language: targetLanguage }, { timeout: 60000 });
export const getMyAIJobs = (limit = 20) => api.get('/ai/jobs', { params: { limit } });

// AI Style Transfer & Enhancement
export const getArtStyles = () => api.get('/ai/styles');
export const getEnhancementOptions = () => api.get('/ai/enhancements');
export const styleTransfer = (imageBase64, style, intensity = 'medium') => 
  api.post('/ai/style-transfer', { image_base64: imageBase64, style, intensity }, { timeout: 120000 });
export const enhancePanel = (imageBase64, enhancements, preserveStyle = true) => 
  api.post('/ai/enhance', { image_base64: imageBase64, enhancements, preserve_style: preserveStyle }, { timeout: 120000 });

// PDF Export
export const getComicPdfUrl = (comicId, episodes = 'all') => 
  `${BACKEND_URL}/api/export/comic/${comicId}/pdf?episodes=${episodes}`;
export const getEpisodePdfUrl = (episodeId) => 
  `${BACKEND_URL}/api/export/episode/${episodeId}/pdf`;

// Blog
export const getBlogPosts = (params) => api.get('/blog/posts', { params });
export const getBlogPost = (id) => api.get(`/blog/posts/${id}`);
export const getBlogCategories = () => api.get('/blog/categories');
export const likeBlogPost = (id) => api.post(`/blog/posts/${id}/like`);

// Admin Blog
export const createBlogPost = (data) => api.post('/admin/blog/posts', data);
export const updateBlogPost = (id, data) => api.put(`/admin/blog/posts/${id}`, data);
export const deleteBlogPost = (id) => api.delete(`/admin/blog/posts/${id}`);

// Seed
export const seedDatabase = () => api.post('/seed');

// ============= SMART REVENUE SHARING =============
// Engagement Tracking
export const trackEngagement = (data) => api.post('/engagement/track', data);
export const getMyEngagementActivity = (days = 30) => api.get('/engagement/my-activity', { params: { days } });

// Auto-Allocation & Receipts
export const triggerAutoAllocation = () => api.post('/donations/auto-allocate');
export const getMonthlyReceipt = (month = null) => api.get('/donations/monthly-receipt', { params: { month } });
export const getReceiptsHistory = (limit = 12) => api.get('/donations/receipts-history', { params: { limit } });

// Creator Pool Settings
export const getCreatorPoolSettings = () => api.get('/creator/pool-settings');
export const updateCreatorPoolSettings = (settings) => api.put('/creator/pool-settings', settings);

// User Boost Settings
export const getBoostSettings = () => api.get('/donations/boost-settings');
export const updateBoostSettings = (settings) => api.put('/donations/boost-settings', settings);

// Undiscovered Creators
export const getUndiscoveredCreators = (limit = 10) => api.get('/creators/undiscovered', { params: { limit } });

// Admin Abuse Management
export const getAbuseFlags = (status = 'pending_review', limit = 50) => 
  api.get('/admin/abuse-flags', { params: { status, limit } });
export const reviewAbuseFlag = (flagId, action) => 
  api.put(`/admin/abuse-flags/${flagId}`, null, { params: { action } });

// ============= AD REVENUE SHARING =============
export const trackAdEvent = (data) => api.post('/ads/track', data);
export const getCreatorAdEarnings = (period = '30d') => api.get('/ads/creator-earnings', { params: { period } });
export const getComicAdStats = (comicId) => api.get(`/ads/comic/${comicId}/stats`);
export const getAdminAdOverview = (period = '30d') => api.get('/admin/ads/overview', { params: { period } });

// ============= PAID CONTENT & PURCHASES =============
export const setEpisodePrice = (episodeId, price) => api.put(`/episodes/${episodeId}/price`, { price });
export const makeEpisodeFree = (episodeId) => api.put(`/episodes/${episodeId}/make-free`);
export const checkEpisodeAccess = (episodeId) => api.get(`/episodes/${episodeId}/access`);
export const createChapterCheckout = (episodeId, originUrl) => 
  api.post('/purchases/chapter/checkout', { episode_id: episodeId, origin_url: originUrl });
export const createBundleCheckout = (comicId, originUrl) => 
  api.post('/purchases/bundle/checkout', { comic_id: comicId, origin_url: originUrl });
export const getMyPurchases = () => api.get('/purchases/my-purchases');
export const getCreatorSales = (period = '30d') => api.get('/creator/sales', { params: { period } });
export const getComicBundleInfo = (comicId) => api.get(`/comics/${comicId}/bundle-info`);

// Episode Access Control
export const checkComicEpisodesAccess = (comicId) => api.get(`/comics/${comicId}/episodes/access`);
export const checkEpisodeAccessPublic = (episodeId) => api.get(`/episodes/${episodeId}/access/public`);

// Admin Plan Reordering
export const reorderSubscriptionPlans = (plans) => api.post('/admin/subscription-plans/reorder', { plans });

// ============= TIPPING / SUPER LIKES =============
export const createTipCheckout = (data) => api.post('/tips/checkout', data);
export const getTipAmounts = () => api.get('/tips/amounts');
export const getReceivedTips = (period = '30d') => api.get('/tips/received', { params: { period } });
export const getSentTips = () => api.get('/tips/sent');
export const getCreatorTipsSummary = (creatorId) => api.get(`/creators/${creatorId}/tips-summary`);
export const getTippingLeaderboard = (period = '30d', limit = 20) => 
  api.get('/tips/leaderboard', { params: { period, limit } });
export const createSuperLike = (creatorId, comicId, episodeId, amount, originUrl) => 
  api.post('/super-like', null, { params: { creator_id: creatorId, comic_id: comicId, episode_id: episodeId, amount, origin_url: originUrl } });


// ============= CREATOR-ADMIN MESSAGING =============
export const getCreatorAdminMessages = () => api.get('/creator/admin-messages');
export const getCreatorUnreadCount = () => api.get('/creator/admin-messages/unread-count');
export const sendMessageToAdmin = (content) => api.post('/creator/admin-messages', { content });

// ============= USER COLLECTIONS =============
export const getMyCollections = () => api.get('/collections');
export const getPublicCollections = (limit = 20) => api.get(`/collections/public?limit=${limit}`);
export const getCollection = (id) => api.get(`/collections/${id}`);
export const createCollection = (data) => api.post('/collections', data);
export const updateCollection = (id, data) => api.put(`/collections/${id}`, data);
export const deleteCollection = (id) => api.delete(`/collections/${id}`);
export const addToCollection = (collectionId, comicId) => api.post(`/collections/${collectionId}/add/${comicId}`);
export const removeFromCollection = (collectionId, comicId) => api.delete(`/collections/${collectionId}/remove/${comicId}`);

// ============= CREATOR QUICK STATS =============
export const getCreatorQuickStats = () => api.get('/creator/quick-stats');

// Aliases for backward compatibility (Artist = Creator)
export const getArtists = getCreators;
export const getArtist = getCreator;

// Fan Art (placeholder - not yet implemented on backend)
export const getFanArtRequests = (params) => api.get('/fanart/requests', { params });
export const getMyFanArtRequests = () => api.get('/fanart/my-requests');
export const createFanArtRequestCheckout = (data) => api.post('/fanart/checkout', data);

export default api;
